import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { UploadScreen } from 'src/components/pages/UploadCases/UploadCases';
import {
  BulkImportCases,
  BulkImportCasesDynamic,
  useBulkCaseCreationMutation,
  useGetCaseImporterJobProgressQuery,
  useValidateCasesCsvUploadMutation,
  ValidateCasesCsvUploadMutation,
} from 'src/graphql';
import {
  CsvImportSuccess,
  FeatureFlag,
  getReadableId,
  mapCsvRecordToBulkImportCasesDynamic,
  State,
} from 'src/types';
import {
  trackCaseImportCsvValidationFailedEvent,
  trackCaseImportProcessingFailedToStart,
} from 'src/utils/analytics/track/caseImport';
import { DateFormats, formatDate } from 'src/utils/time';
import * as Types from '../../types';

type InternalNamedOffenderUser = {
  id: number;
  name: string | undefined;
};

export type EvidenceItem = {
  folderName: string;
  filename: string;
  validated: boolean;
  uploadStatus: string;
  presignedUrl: string;
  s3path: string;
  mimeType: string;
  size: number;
  fileExtension: string;
  error?: string;
};

export enum Template {
  CASES_UPLOAD = 'cases_upload',
  CASES_UPLOAD_DYNAMIC = 'cases_upload_dynamic_questions',
}

export const useUploadCases = () => {
  const [loading, setLoading] = useState(false);
  const [screen, setScreen] = useState(UploadScreen.PRE_IMPORT_CHECKLIST);
  const [template, setTemplate] = useState(Template.CASES_UPLOAD);
  const [casesData, setCasesData] = useState<
    ValidateCasesCsvUploadMutation['validateCasesCsvUpload']
  >({
    totalCasesInCSV: 0,
    casesToCreate: [],
    duplicateCases: [],
    importFileName: '',
  });
  const [importedCaseIds, setImportedCaseIds] = useState<string[]>([]);
  const [uploadEvidences, setUploadEvidences] = useState<EvidenceItem[]>([]);
  const [dynamicCategory, setDynamicCategory] = useState<string>('');
  const [dynamicFormId, setDynamicFormId] = useState<string | null>(null);

  const features = useSelector(({ features }: State) => features);
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const { data: activeJobData } = useGetCaseImporterJobProgressQuery();
  const activeJob = activeJobData?.getCaseImporterJobProgress;
  const hasActiveJob: boolean =
    (activeJob &&
      (activeJob.status === 'PENDING' ||
        activeJob.status === 'IN_PROGRESS' ||
        activeJob.status === 'CANCELLING')) ||
    false;
  const activeJobPercentage: number = activeJob?.percentComplete ?? 0;

  const stateProperties = useSelector(
    (state: Types.State) => ({
      organisationId: state.organisation.organisation?.id || null,
      userId: state.globalUsers.currentUser?.id || null,
      userSystemRole:
        state.globalUsers.currentUser?.SystemRoles.map(
          (role) => role.name,
        ).join(', ') || null,
    }),
    shallowEqual,
  );

  const isDynamicTemplate = template === Template.CASES_UPLOAD_DYNAMIC;
  const { mutate: upload, isSuccess: isValidated } =
    useValidateCasesCsvUploadMutation({
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: (uploadedData) => {
        setCasesData(uploadedData.validateCasesCsvUpload);
        setImportedCaseIds(
          uploadedData.validateCasesCsvUpload.casesToCreate
            .map((caseInstance) => caseInstance.caseImportedId)
            .filter(
              (id): id is string => Boolean(id) && typeof id === 'string',
            ),
        );
        setLoading(false);
        setScreen(UploadScreen.CASE_REVIEW);
      },
      onError: (error) => {
        if (segmentEnabled && stateProperties.organisationId) {
          trackCaseImportCsvValidationFailedEvent({
            organisation_id: Number(stateProperties.organisationId),
            user_id: String(stateProperties.userId),
            user_system_role: stateProperties.userSystemRole,
          });
        }
        const message =
          error instanceof Error
            ? error.message
            : 'We encountered a problem while uploading your CSV. Please try again.';
        handleErrorToast({
          toastMessage: 'Oops!',
          toastDescription: message,
        });
        setLoading(false);
      },
    });

  const onImportSuccess = (data: CsvImportSuccess) => {
    const csvRecords = [
      ...data.records,
      ...data.error_records.map(({ data: record }) => record),
    ];

    const newCases = isDynamicTemplate
      ? (csvRecords as BulkImportCasesDynamic[]).map(
          mapCsvRecordToBulkImportCasesDynamic,
        )
      : (csvRecords as BulkImportCases[]);
    upload({
      newCases: !isDynamicTemplate ? (newCases as BulkImportCases[]) : [],
      newCasesDynamic: isDynamicTemplate
        ? (newCases as BulkImportCasesDynamic[])
        : [],
      sheetId: data.sheet_id,
      embedId: data.embed_id,
      importFileName: data.sheet_metadata.original_file_name,
      columnMapping: data.columns,
      dynamicCategory: isDynamicTemplate ? dynamicCategory : undefined,
      dynamicFormId: isDynamicTemplate ? dynamicFormId : undefined,
    });
  };

  const { mutate: bulkCaseCreation } = useBulkCaseCreationMutation({
    onSuccess: () => {
      setScreen(UploadScreen.IMPORT_PROGRESS);
      handleSuccessToast({
        toastMessage: 'Import started',
        toastDescription: `Your import of ${casesData.casesToCreate.length} case(s) has been initiated and is now processing in the background. You can track the real-time progress of your import under the Import History page.`,
      });
    },
    onError: (error) => {
      if (segmentEnabled && stateProperties.organisationId) {
        trackCaseImportProcessingFailedToStart({
          organisation_id: Number(stateProperties.organisationId),
          user_id: String(stateProperties.userId),
          user_system_role: stateProperties.userSystemRole,
        });
      }
      const message =
        error instanceof Error
          ? error.message
          : 'We encountered a problem while importing your cases. Please try again.';
      handleErrorToast({
        toastMessage: 'Oops!',
        toastDescription: message,
      });
    },
  });

  const commit = () => {
    return bulkCaseCreation({
      casesToCreate: casesData.casesToCreate.map(
        ({ countryName, regionName, ...rest }) => rest, // Omit countryName and regionName as they are not needed for the mutation input
      ),
      importFileName: casesData.importFileName,
      uploadEvidences: uploadEvidences
        .filter((item) => item.validated && item.uploadStatus === 'Success')
        .map((item) => ({
          caseImportedId: item.folderName,
          s3path: item.s3path,
          mimeType: item.mimeType,
          size: item.size,
          fileExtension: item.fileExtension,
          filename: item.filename,
        })),
    });
  };

  const getDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return '-';
    return formatDate(dateString, DateFormats.DAY_MONTH_YEAR);
  };

  const tableColumns = [
    'Validation Status',
    'Case Name',
    'Original Case ID',
    'Created Date',
    'Submission Date',
    'Investigating Date',
    'Closed Date',
    'Case Lead',
    'Case Categories',
    'Status',
    'Substantiation Status',
    'Original Intake Method',
    'Is Anonymous',
    'Reporter',
    'Case country',
    'Case region',
    'Case summary',
    'Named Offender External',
    'Named Offender Internal',
    'Event date',
    'Event time',
    'Event location',
    'Event description',
    'Event witnesses',
    'Event emotional impact',
  ];

  if (isDynamicTemplate) {
    // Remove event-related columns for dynamic template as they may not be relevant, and replace with dynamic field columns
    const dynamicFieldColumns = Object.keys(
      casesData.casesToCreate[0]?.dynamicFields ?? {},
    );
    tableColumns.splice(19, 6, ...dynamicFieldColumns);
  }

  const tableData = casesData.casesToCreate.map((cell, index) => {
    const cellData = [
      'New',
      cell.caseName,
      cell.caseImportedId,
      getDate(cell.caseCreatedDate),
      getDate(cell.caseSubmissionDate),
      getDate(cell.caseInvestigatingDate),
      getDate(cell.caseClosedDate),
      cell.caseLeadName,
      cell.caseCategories?.join(', '),
      cell.caseStatus,
      cell.substantiationStatus,
      cell.originalIntakeMethod,
      cell.isAnonymous,
      cell.reporterName ?? cell.reporterEmail,
      cell.countryName,
      cell.regionName,
      cell.caseSummary,
      cell.namedOffenderExternalUsers?.join(', '),
      (cell.namedOffenderInternalUsers as InternalNamedOffenderUser[])
        ?.map((user: InternalNamedOffenderUser) => user.name)
        .filter(Boolean)
        .join(', '),
      cell.eventDate,
      cell.eventTime,
      cell.eventLocation,
      cell.eventDescription.join('\n'),
      cell.eventWitnesses,
      cell.eventEmotions,
    ];
    if (isDynamicTemplate) {
      const dynamicFieldValues = Object.values(
        cell.dynamicFields ?? {},
      ) as string[];
      cellData.splice(19, 6, ...dynamicFieldValues);
    }
    return {
      cellData,
      row: `index-${index}`,
      data: {},
    };
  });

  const duplicateTableColumns = [
    'Validation Status',
    'Case Name',
    'Original Case ID',
    'Case ID',
    'Status',
    'Created Date',
    'Investigating Date',
    'Closed Date',
  ];
  const duplicateTableData = casesData.duplicateCases.map((cell, index) => {
    return {
      cellData: [
        'Duplicate',
        cell.name,
        cell.importedId,
        getReadableId(cell.id),
        cell.status,
        getDate(cell.CaseAttribute.createdAt),
        getDate(cell.CaseAttribute.investigatingAt),
        getDate(cell.CaseAttribute.closedAt),
      ],
      row: `index-${index}`,
      data: {},
    };
  });

  return {
    loading,
    setLoading,
    screen,
    setScreen,
    onImportSuccess,
    isValidated,
    tableData,
    tableColumns,
    duplicateTableData,
    duplicateTableColumns,
    commit,
    hasActiveJob,
    activeJobPercentage,
    importedCaseIds,
    uploadEvidences,
    setUploadEvidences,
    stateProperties,
    importFileName: casesData.importFileName,
    totalCasesInCSV: casesData.totalCasesInCSV,
    template,
    setTemplate,
    dynamicCategory,
    setDynamicCategory,
    dynamicFormId,
    setDynamicFormId,
  };
};
